// Portfolio Administration v1.0 PR 5 — sticky header for the Local
// Enforcer registry detail page. Renders title (legal name + DBA) +
// status pill + type badge + assigned E1 sponsor + global "Change
// status" affordance. Gated by E1 / ADMIN role codes per spec §14.

import { useState } from "react";
import { Button, Card } from "@/design-system/components/ui";
import type { LocalEnforcerDetailDto } from "@/api/portfolioAdmin/pr4bTypes";
import { useAuth } from "@/hooks/useAuth";
import { useUpdateLocalEnforcerStatus } from "@/hooks/portfolioAdmin/useRegistries";
import { RegistryStatusPill } from "@/components/portfolioAdmin/registry/RegistryStatusPill";

const STATUS_TRANSITIONS = [
  "prospect",
  "onboarding",
  "active",
  "suspended",
  "terminated",
];

const TYPE_LABELS: Record<string, string> = {
  "law-firm": "Law firm",
  "collection-agency": "Collection agency",
  "receiver-trustee": "Receiver / trustee",
  "process-server": "Process server",
  auctioneer: "Auctioneer",
  liquidator: "Liquidator",
  "appraiser-for-recovery": "Appraiser (recovery)",
  locksmith: "Locksmith",
  "security-firm": "Security firm",
  "repossession-firm": "Repossession firm",
  "skip-tracer": "Skip-tracer",
  investigator: "Investigator",
};

export interface LocalEnforcerDetailHeaderProps {
  enforcer: LocalEnforcerDetailDto;
}

export function LocalEnforcerDetailHeader({
  enforcer,
}: LocalEnforcerDetailHeaderProps) {
  const auth = useAuth();
  const canManage =
    auth.hasRoleCode("E1") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Enforcer.Manage");
  const update = useUpdateLocalEnforcerStatus(enforcer.id);
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(enforcer.status);
  const [reason, setReason] = useState("");

  const submit = async () => {
    await update.mutateAsync({ toStatus: next, reason: reason || null });
    setEditing(false);
    setReason("");
  };

  return (
    <Card
      className="flex flex-col gap-space-2"
      data-testid="enforcer-detail-header"
    >
      <div className="flex items-start justify-between gap-space-3">
        <div className="flex flex-col gap-space-1">
          <h1 className="font-display font-bold text-h1 m-0">
            {enforcer.legalEntityName}
          </h1>
          {enforcer.dbaName ? (
            <span className="text-small text-gray-mid">
              DBA {enforcer.dbaName}
            </span>
          ) : null}
          <div className="flex items-center gap-space-2 flex-wrap">
            <RegistryStatusPill status={enforcer.status} />
            <span className="text-small text-gray-mid">
              {TYPE_LABELS[enforcer.enforcerType] ?? enforcer.enforcerType}
            </span>
            {enforcer.assignedE1OwnerId ? (
              <span className="text-small text-gray-mid">
                · E1 sponsor:{" "}
                <span className="font-semibold text-onyx">
                  {enforcer.assignedE1OwnerId}
                </span>
              </span>
            ) : null}
          </div>
        </div>
        {canManage ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setEditing((v) => !v)}
            data-testid="enforcer-change-status"
          >
            Change status
          </Button>
        ) : null}
      </div>
      {editing ? (
        <div className="flex flex-wrap items-end gap-space-2 border-t border-border-light pt-space-2">
          <div className="flex flex-col gap-space-1">
            <label className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              New status
            </label>
            <select
              className="px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="enforcer-status-select"
            >
              {STATUS_TRANSITIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-space-1 flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              Reason (audit note)
            </label>
            <input
              type="text"
              className="px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="enforcer-status-reason"
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={update.isPending}
            data-testid="enforcer-status-submit"
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="tertiary"
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
